import React from 'react';
import { getAssetColorPalette } from '../../utils/assetHelpers';

interface CryptoCarProps {
  symbol: string;
  isVertical?: boolean; // For vertical racing layout
  showExhaustStreaks?: boolean; // Show hyperspeed streaks during racing
  isRacing?: boolean; // Enable racing drift animation
  driftPattern?: 1 | 2; // Different drift timing patterns for variety
  laneIndex?: number; // Lane position (0 = left, 1 = right) for horizontal perspective skew
}

const CryptoCar: React.FC<CryptoCarProps> = ({ 
  symbol, 
  isVertical = false, 
  showExhaustStreaks = false,
  isRacing = false,
  driftPattern,
  laneIndex = 0
}) => {
  // Get color palette from centralized asset helpers (single source of truth)
  const palette = getAssetColorPalette(symbol);
  
  // Generate accent color (lighter than secondary for highlights)
  const lightenColor = (hex: string): string => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    
    const lighten = (val: number) => Math.min(255, Math.floor(val + (255 - val) * 0.3));
    
    return `#${lighten(r).toString(16).padStart(2, '0')}${lighten(g).toString(16).padStart(2, '0')}${lighten(b).toString(16).padStart(2, '0')}`;
  };
  
  const colors = {
    primary: palette.primary,
    secondary: palette.secondary,
    accent: lightenColor(palette.secondary),
    dark: palette.dark
  };

  // Build className for racing drift
  const containerClasses = [
    'crypto-car-container',
    'svg-car',
    isRacing && 'racing-drift',
    isRacing && driftPattern && `drift-pattern-${driftPattern}`,
    isRacing && `lane-${laneIndex}` // Add lane-specific class for horizontal perspective
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClasses}>
      {/* SVG Car - Dynamic Colors Based on Crypto */}
      <svg width="66" height="87" viewBox="0 0 66 87" fill="none" xmlns="http://www.w3.org/2000/svg" className="car-svg">
        {/* Wheels */}
        <rect x="1" y="18" width="9" height="16" rx="3" fill="#291012" stroke={colors.primary} strokeWidth="2"/>
        <rect x="56" y="18" width="9" height="16" rx="3" fill="#291012" stroke={colors.primary} strokeWidth="2"/>
        <rect x="1" y="55" width="9" height="16" rx="3" fill="#291012" stroke={colors.primary} strokeWidth="2"/>
        <rect x="56" y="55" width="9" height="16" rx="3" fill="#291012" stroke={colors.primary} strokeWidth="2"/>
        
        {/* Outer Body */}
        <defs>
          <radialGradient id={`paint0_${symbol}`} cx="0" cy="0" r="1" gradientTransform="matrix(-3 44.7692 -34.1099 -1.94759 34 42)" gradientUnits="userSpaceOnUse">
            <stop stopColor={colors.dark}/>
            <stop offset="1" stopColor={colors.secondary}/>
          </radialGradient>
          <radialGradient id={`paint1_${symbol}`} cx="0" cy="0" r="1" gradientTransform="matrix(-2.25 44.7692 -25.5824 -1.94759 34 42)" gradientUnits="userSpaceOnUse">
            <stop stopColor={colors.accent}/>
            <stop offset="1" stopColor={colors.primary}/>
          </radialGradient>
          <linearGradient id={`paint2_${symbol}`} x1="29" y1="110.5" x2="54.5" y2="65.5" gradientUnits="userSpaceOnUse">
            <stop stopColor={colors.primary}/>
            <stop offset="1" stopColor={colors.dark}/>
          </linearGradient>
          <linearGradient id={`paint3_${symbol}`} x1="34" y1="49.0111" x2="25.7727" y2="6.49587" gradientUnits="userSpaceOnUse">
            <stop stopColor="#10021B"/>
            <stop offset="1" stopColor={colors.primary}/>
          </linearGradient>
        </defs>
        
        <path d="M24.6709 4.6582C30.6762 2.34966 37.3238 2.34966 43.3291 4.6582L58.3789 10.4434C61.9164 11.8036 63.729 15.7325 62.4678 19.3066L59.5088 27.6904C56.2412 36.9501 56.2412 47.0499 59.5088 56.3096L62.9072 65.9385C64.0047 69.0486 62.782 72.5037 59.9727 74.2314L57.9473 75.4766C52.7924 78.6467 47.0737 80.7923 41.1055 81.7939L38.4473 82.2393C35.5078 82.7326 32.5054 82.7176 29.5713 82.1934L26.959 81.7266C21.5928 80.7678 16.434 78.8821 11.7139 76.1553L7.95215 73.9824C5.15329 72.3655 3.82527 69.0419 4.73926 65.9414L7.90918 55.1885C10.1291 47.6578 10.245 39.6631 8.24414 32.0713L4.79395 18.9795C3.88433 15.5271 5.71925 11.9432 9.05176 10.6621L24.6709 4.6582Z" fill={`url(#paint0_${symbol})`} stroke={colors.primary} strokeWidth="2"/>
        
        <path d="M22.1406 7.20215C29.5863 3.38588 38.4137 3.38588 45.8594 7.20215L51.8105 10.2529C54.739 11.7539 56.2267 15.0922 55.3848 18.2734L52.0176 30.998C50.1093 38.2084 50.1093 45.7916 52.0176 53.002L55.8379 67.4385C56.4657 69.8108 55.6515 72.3316 53.7539 73.8877L52.0811 75.2588C48.0333 78.5778 43.2634 80.9018 38.1553 82.0449C35.426 82.6556 32.583 82.6346 29.8613 81.9863C25.2267 80.8822 20.8635 78.8042 17.0898 75.8975L14.6367 74.0078C12.4866 72.3514 11.4873 69.6013 12.0732 66.9512L15.2773 52.4629C16.559 46.6659 16.6259 40.6661 15.4746 34.8418L12.1514 18.0264C11.5454 14.9608 13.0444 11.8648 15.8252 10.4395L22.1406 7.20215Z" fill={`url(#paint1_${symbol})`} stroke={colors.primary} strokeWidth="2"/>
        
        {/* Windshield - lighter to reduce dark shadow appearance */}
        <path d="M34 23C42.8366 23 50 30.1634 50 39V49C50 57.8366 42.8366 65 34 65C25.1634 65 18 57.8366 18 49V39C18 30.1634 25.1634 23 34 23Z" fill="rgba(16, 2, 27, 0.7)" stroke={colors.primary} strokeWidth="2"/>
        
        {/* Spoiler */}
        <path d="M54.8613 73.5C55.7473 73.5 56.5626 73.9683 57.0107 74.7227L57.0957 74.8779L60.6123 81.8779C61.4473 83.5403 60.2392 85.4997 58.3789 85.5H9.71387C7.79953 85.5 6.59515 83.4361 7.53711 81.7695L11.4941 74.7695L11.582 74.626C12.0422 73.9264 12.8261 73.5 13.6709 73.5H54.8613Z" fill={`url(#paint2_${symbol})`} stroke={colors.primary} strokeWidth="3"/>
        
        {/* Center Windshield Detail */}
        <path d="M34 29C38.9706 29 43 33.0294 43 38V49C43 53.9706 38.9706 58 34 58C29.0294 58 25 53.9706 25 49V38C25 33.0294 29.0294 29 34 29Z" fill={`url(#paint3_${symbol})`} stroke={colors.primary} strokeWidth="2"/>
        
        {/* Windshield Glare - Simplified without filter to avoid shadow artifacts */}
        <path d="M16.1527 19.4092C15.5228 18.4757 15.7871 17.2037 16.7701 16.6542C23.6302 12.8194 35.9809 8.45714 50.2345 16.6415C51.2116 17.2025 51.4672 18.483 50.8254 19.409C50.2154 20.2892 49.0168 20.5205 48.0868 19.9893C35.6686 12.8958 25.0387 16.5813 18.8748 20.025C17.9401 20.5472 16.7515 20.2968 16.1527 19.4092Z" fill="white" opacity="0.9"/>
      </svg>
      
      {/* Tron-Style Persistent Exhaust Trails - Always on */}
      {showExhaustStreaks && (
        <div className="exhaust-trails">
          {/* Left trail from rear tire */}
          <div className="exhaust-trail exhaust-trail-left" style={{ '--trail-color': colors.primary } as React.CSSProperties}>
            <div className="trail-flow trail-flow-1"></div>
            <div className="trail-flow trail-flow-2"></div>
            <div className="trail-flow trail-flow-3"></div>
            {/* Particles around left trail */}
            <div className="trail-particle particle-1"></div>
            <div className="trail-particle particle-2"></div>
            <div className="trail-particle particle-3"></div>
          </div>
          
          {/* Right trail from rear tire */}
          <div className="exhaust-trail exhaust-trail-right" style={{ '--trail-color': colors.primary } as React.CSSProperties}>
            <div className="trail-flow trail-flow-1"></div>
            <div className="trail-flow trail-flow-2"></div>
            <div className="trail-flow trail-flow-3"></div>
            {/* Particles around right trail */}
            <div className="trail-particle particle-1"></div>
            <div className="trail-particle particle-2"></div>
            <div className="trail-particle particle-3"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CryptoCar;

